import {
  KeyboardEvent as KBEvent,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef
} from 'react'
import ReactPortal from './ReactPortal'
import { CloseIcon } from './Icons'
import IconButton from './buttons/IconButton'
import { classNames } from 'primereact/utils'

type ModalProps = {
  onCancel?: () => void
  onConfirm?: () => void
  onClose?: () => void
  isOpen: boolean
  easyClose?: boolean
  showClose?: boolean
  withAction: boolean
  title: string
  showCancelButton?: boolean
  cancelButtonDisabled?: boolean
  confirmButtonDisabled?: boolean
  cancelButtonCaption?: string
  confirmButtonCaption?: string
  zIndex?: number
  size?: 'sm' | 'md' | 'lg' | 'xl'
  footer?: ReactElement
  titleAdditionalClass?: string
}

const Modal = ({
  title,
  isOpen,
  zIndex = 20,
  size = 'lg',
  withAction = true,
  showCancelButton = true,
  cancelButtonDisabled = false,
  confirmButtonDisabled = false,
  cancelButtonCaption = 'Cancel',
  confirmButtonCaption = 'OK',
  easyClose = false,
  showClose = true,
  onConfirm,
  onCancel,
  onClose,
  children,
  footer,
  titleAdditionalClass
}: ModalProps & PropsWithChildren) => {
  // Tracks whether the pointer press that led to a click originated on the backdrop. Without
  // this, selecting text inside the modal and releasing the mouse outside fires a click on the
  // backdrop container and wrongly dismisses the modal.
  const mouseDownedOnBackdrop = useRef(false)

  useEffect(() => {
    const handelKeydown = (evt: KeyboardEvent) => {
      if (evt.code === 'Escape') {
        onClose && onClose()
      }
    }
    document.addEventListener('keydown', handelKeydown)
    return () => {
      document.removeEventListener('keydown', handelKeydown)
    }
  }, [])

  if (!isOpen) {
    return null
  }

  const handleBackdropMouseDown = (evt: MouseEvent<HTMLDivElement>) => {
    mouseDownedOnBackdrop.current = (evt.target as HTMLDivElement).id === 'outside-container'
  }

  const handleClickOutside = (evt: MouseEvent<HTMLDivElement>) => {
    const div = evt.target as HTMLDivElement
    const startedOnBackdrop = mouseDownedOnBackdrop.current
    mouseDownedOnBackdrop.current = false
    div.id === 'outside-container' && startedOnBackdrop && easyClose && onClose && onClose()
  }

  const handleKeyDown = (evt: KBEvent<HTMLDivElement>) => {
    easyClose && evt.code === 'Escape' && onClose && onClose()
  }

  return (
    <ReactPortal wrapperId="react-portal-modal-container">
      <div
        className="fixed opacity-90 bg-dark-blue dark:bg-neutral-80 inset-0 ease-in"
        style={{ zIndex: zIndex }}></div>
      <div
        id="outside-container"
        className="flex justify-center items-center rounded-md fixed shadow-xl inset-0 dark:text-neutral"
        onMouseDown={handleBackdropMouseDown}
        onClick={handleClickOutside}
        style={{ zIndex: 1 + zIndex }}
        onKeyDown={handleKeyDown}>
        <div
          id="dialog-content"
          className={classNames(
            'bg-white dark:bg-neutral-90 p-6 flex flex-col items-stretch max-h-[90vh] max-w-7xl mx-auto rounded-lg',
            {
              'w-[90%]': size === 'xl',
              'w-[80%]': size === 'lg',
              'w-[50%]': size === 'md',
              'w-[300px]': size === 'sm'
            }
          )}>
          <div className="flex items-center justify-between flex-shrink-0">
            <h2
              className={`flex items-center text-xl text-dark-blue dark:text-neutral-10 ${titleAdditionalClass}`}>
              {title}
            </h2>
            {showClose && (
              <IconButton
                title="Close"
                link
                icon={<CloseIcon className="w-6 h-6 fill-black dark:fill-light-blue" />}
                onClick={() => {
                  onCancel && onCancel()
                }}
              />
            )}
          </div>
          <div className="flex flex-col items-stretch flex-1 overflow-y-auto min-h-0">
            {children}
          </div>
          {footer && <div className="basis-10">{footer}</div>}
          {withAction && (
            <div className="flex items-center justify-end basis-10 space-x-4 pt-4">
              <button
                disabled={confirmButtonDisabled}
                onClick={() => {
                  onConfirm && onConfirm()
                }}
                className="px-5 py-2 rounded-lg bg-gm-blue hover:bg-dark-blue text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition">
                {confirmButtonCaption}
              </button>
              {showCancelButton && (
                <button
                  disabled={cancelButtonDisabled}
                  onClick={() => {
                    onCancel && onCancel()
                  }}
                  className="px-5 py-2 rounded-lg bg-gray-700 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-200 text-white dark:text-black text-sm font-medium transition">
                  {cancelButtonCaption}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </ReactPortal>
  )
}

export default Modal
